import * as CL from '@design-system/component-library';
import { HYPHEN } from '../../common/constants/commonConstants.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { RelatedPartyRoles } from '../../common/enums.js';
import {
  approverMsg,
  billingAccountMsg,
  changeRequestorMsg,
  detailsChangeMsg,
  statusMsg,
  t,
  timeOfChangeMsg,
  topicMsg,
} from '../../common/i18n/index.js';
import { formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils.js';
import { capitalize } from '../../common/utils/stringUtils.js';
import { getOrderStateInfo, getPartyName, getTopicText } from '../CustomerOrderList/cocoOrderUtils.js';
import { Status } from '../Status/Status.js';
import { formatPrice, getValue } from './OrderContentCoCoUtils.js';
import type { CLDescriptionItem } from '../../common/types/componentLibrary.js';
import type { CocoProductOrderDetail } from '../../generated/api/cocoProductOrderDetail.js';

const ChangeOrderBasicInfo = ({ order }: { order: CocoProductOrderDetail }) => {
  const orderStatusInfo = getOrderStateInfo(order.orderState);
  const fields: CLDescriptionItem[] = [
    {
      title: t.ASQT(statusMsg),
      description: <Status color={orderStatusInfo.color} textAfterBadge={orderStatusInfo.text} />,
    },
    {
      title: t.Q1DM(timeOfChangeMsg),
      description: formatTimestampToDDMMYYYY(Date.parse(order.created)),
    },
    {
      title: t.ARD5(approverMsg),
      description: getPartyName(order, RelatedPartyRoles.APPROVER),
    },
    {
      title: t.C8M7(changeRequestorMsg),
      description: getPartyName(order, RelatedPartyRoles.ASSIGNEE),
    },
    { title: t.JJTS(topicMsg), description: getTopicText(order) },
  ];

  return <CL.Description items={fields} className={dsClass.PADDING_BOTTOM_5} />;
};

const ChangeOrderDetailedInfo = ({ order }: { order: CocoProductOrderDetail }) => {
  const fields: CLDescriptionItem[] = [];
  const { changeDetails } = order;

  const addField = (title: string, description?: string) => {
    if (description) {
      fields.push({ title, description });
    }
  };

  addField(t.UPZV('The subscription affected by the change'), order.affectedSubscription?.subscriptionId);
  addField(t.IFT9(billingAccountMsg), order.billingAccount?.name);

  if (changeDetails?.subscriptionType) {
    addField(t.SK41('Old subscription type'), changeDetails.subscriptionType.old.name);
    addField(t.XEUL('New subscription type'), changeDetails.subscriptionType._new.name);
  }

  if (changeDetails?.price) {
    const subscriptionText = t.D2UO('subscription');
    addField(
      capitalize(t.TWB3('The price of the {} before the change', subscriptionText)),
      formatPrice(changeDetails.price.old)
    );
    addField(
      capitalize(t.AHN6('The price of the {} after the change', subscriptionText)),
      formatPrice(changeDetails.price._new)
    );
  }

  if (changeDetails?.services) {
    addField(t.CYXH('Old services'), changeDetails.services.old.map(service => service.name).join(', ') || HYPHEN);
    addField(t.GRFB('New services'), changeDetails.services._new.map(service => service.name).join(', ') || HYPHEN);
  }

  if (changeDetails?.costCentre) {
    addField(t.MN4H('Old cost center'), getValue(changeDetails.costCentre.old));
    addField(t.MRG5('New cost center'), getValue(changeDetails.costCentre._new));
  }

  if (changeDetails?.reference) {
    addField(t.JD6A('Old reference'), getValue(changeDetails.reference.old));
    addField(t.BIRR('New reference'), getValue(changeDetails.reference._new));
  }

  if (changeDetails?.user) {
    addField(t.FVC8('Old user'), changeDetails.user.old.name);
    addField(t.ON49('New user'), changeDetails.user._new.name);
  }

  return (
    <div>
      <h3 className={`${dsClass.MARGIN_BOTTOM_5} ${dsClass.MARGIN_TOP_6}`}>{t.E9BD(detailsChangeMsg)}</h3>
      <CL.Description items={fields} className={dsClass.PADDING_BOTTOM_5} />
    </div>
  );
};

export const ChangeOrderContentCoCo = ({ order }: { order: CocoProductOrderDetail }) => {
  return (
    <div className="of-customer-order-details__content">
      <ChangeOrderBasicInfo order={order} />
      <hr />
      <ChangeOrderDetailedInfo order={order} />
    </div>
  );
};
